import { ROUTE_BUILDER } from '@/constants/pages';
import { getSalonDate, url } from '@/helpers';
import { buildLocalBusinessSchema, jsonLd, placePreviewAdapter } from '@/helpers/jsonld';
import { getSerpSeoDescription, getSerpSeoTitle } from '@/helpers/seoTs';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { Place } from '@/types/state/shared';
import { LocalBusiness, SearchResultsPage } from 'schema-dts';
import {
  DealSearchItem,
  DealsSearchPreviewResponse,
  DealsSearchResultsResponse,
  SistaminutenSearchItem,
} from '../types/dealSearch';

function dealPlaceAdapter(place: DealSearchItem['place']): Place {
  return {
    about: { name: place.name },
    contact: {
      address: place.address,
      position: place.position,
    },
    reviews: {
      reviewCount: place.rating?.count,
      stats: { score: place.rating?.score },
      topReviews: undefined,
    },
  };
}

function groupDealsByPlace(items: DealSearchItem[]): LocalBusiness[] {
  const grouped = new Map<number, { place: DealSearchItem['place']; deals: DealSearchItem[] }>();

  for (const item of items) {
    const existing = grouped.get(item.place.id);
    if (existing) {
      existing.deals.push(item);
    } else {
      grouped.set(item.place.id, { place: item.place, deals: [item] });
    }
  }

  return Array.from(grouped.values()).map(({ place, deals }) => {
    const schema = buildLocalBusinessSchema(dealPlaceAdapter(place));
    schema.url = `${url.getBaseUrl()}places/${place.slug}-${place.id}`;
    if (place.profileImageURL) schema.image = place.profileImageURL;

    const offers = deals
      .filter((d) => d.price != null)
      .map((d) => {
        const dateFields =
          d.startDate != null && d.endDate != null
            ? {
                validFrom: getSalonDate(d.startDate, place.timezone).format(),
                validThrough: getSalonDate(d.endDate, place.timezone).format(),
              }
            : {};

        return {
          '@type': 'Offer' as const,
          name: d.name,
          price: d.price,
          priceCurrency: 'SEK',
          ...dateFields,
        };
      });

    if (offers.length === 1) schema.makesOffer = offers[0];
    else if (offers.length > 1) schema.makesOffer = offers;

    return schema;
  });
}

function buildSmtLocalBusiness(item: SistaminutenSearchItem): LocalBusiness {
  const schema = buildLocalBusinessSchema(placePreviewAdapter(item));
  schema.url = `${url.getBaseUrl()}places/${item.slug}-${item.id}`;
  if (item.profileImageURL) schema.image = item.profileImageURL;
  return schema;
}

function buildItemList(items: LocalBusiness[]) {
  return items.map((item, index) => ({
    '@type': 'ListItem' as const,
    position: index + 1,
    item,
  }));
}

export function getDealsSearchResultsPageSchema(
  preview: DealsSearchPreviewResponse | null,
  list: DealsSearchResultsResponse | null,
  type: BaseSearchManagerOutput['type'],
): string | null {
  let businesses: LocalBusiness[] = [];

  if (type === 'deals_preview' && preview) {
    for (const section of preview.sections) {
      if (section.type === 'deals') {
        businesses.push(...groupDealsByPlace(section.items));
      } else {
        businesses.push(...section.items.map(buildSmtLocalBusiness));
      }
    }
  } else if (type === 'deals' && list?.type === 'deals') {
    const items = list.results.flatMap((r) => (r.type === 'organic' ? r.items : []));
    businesses = groupDealsByPlace(items);
  } else if (type === 'deals_sistaminuten' && list?.type === 'sistaminuten') {
    const items = list.results.flatMap((r) => (r.type === 'organic' ? r.items : []));
    businesses = items.map(buildSmtLocalBusiness);
  }

  if (businesses.length === 0) return null;

  const schema: SearchResultsPage = {
    '@type': 'SearchResultsPage',
    name: 'Local Business Search Results',
    about: 'Search results for local businesses in your area',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: buildItemList(businesses),
    },
  };

  return jsonLd({
    '@context': 'https://schema.org',
    ...schema,
  });
}

export function getSeoProps(search: BaseSearchManagerOutput, location /** React router location */, isExtended = false) {
  let pathname: string = '';

  const _keyword = getKeywordWithSlug(search.q)?.slug || DEFAULT_SEARCH_KEYWORD;
  const _location = getLocationWithSlug(search.location)?.slug || DEFAULT_SEARCH_LOCATION;

  switch (search.type) {
    case 'deals_preview':
      pathname = ROUTE_BUILDER.dealsSerpPreview(_keyword, _location).slice(1);
      break;
    case 'deals':
      pathname = ROUTE_BUILDER.dealsSerp(_keyword, _location).slice(1);
      break;
    case 'deals_sistaminuten':
      pathname = ROUTE_BUILDER.sistaMinutenSerp(_keyword, _location).slice(1);
      break;
  }

  const fullURL = `${url.getBaseUrl()}${pathname}${location.search}`;
  const canonical = `${url.getBaseUrl()}${pathname}`;

  const description = getSerpSeoDescription(search);

  const pageNumber = (!isExtended && search.page) ? Math.max(1, search.page) : 1;
  const title = getSerpSeoTitle({ ...search, page: pageNumber - 1 });

  return {
    title,
    description,
    keywords: '',
    url: fullURL,
    canonical,
    image: url.getBaseImageUrl(),
  };
}
